/**
 * Cómo quiere Jorge que le contesten: leyendo o escuchando.
 *
 * Hay un interruptor en la interfaz, pero un interruptor obliga a acordarse de
 * moverlo. Lo natural, sobre todo hablando, es decirlo dentro del propio
 * mensaje: "contéstame en voz", "esto no me lo leas". Así que se mira el texto
 * y, si lo pide, manda lo que pide — solo para ese turno.
 *
 * Se hace con reglas y no preguntándole al modelo, por tres razones: cuesta
 * cero, tarda cero, y es reproducible. Un modelo decidiendo esto se
 * equivocaría de vez en cuando y no habría forma de explicar por qué.
 */
/**
 * Sin acentos, sin signos y en minúsculas.
 *
 * Lo dictado llega con la puntuación que le parece a la transcripción, y
 * "contéstame en voz." y "contestame en voz" tienen que valer igual.
 */
function comparable(texto) {
    return texto
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9ñ ]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
/** Pide que se lo digan hablando y nada más. */
const SOLO_VOZ = [
    "contestame solo en voz",
    "respondeme solo en voz",
    "contestame solo con voz",
    "solo audio",
    "solo en audio",
    "solo con voz",
    "solo en voz",
    "no me lo escribas",
    "sin texto",
];
/** Pide que se lo digan hablando, además de por escrito. */
const CON_VOZ = [
    "contestame en voz",
    "respondeme en voz",
    "contestame hablando",
    "respondeme hablando",
    "contestame con voz",
    "respondeme con voz",
    "leemelo",
    "leeme la respuesta",
    "dimelo en voz alta",
    "en voz alta",
    "mandame un audio",
    "y en audio",
    "tambien en audio",
];
/** Pide que se callen. */
const SIN_VOZ = [
    "no me lo leas",
    "no me hables",
    "sin voz",
    "sin audio",
    "solo texto",
    "solo por escrito",
    "escribemelo",
    "no me contestes en voz",
];
/**
 * Qué pide este mensaje, si es que pide algo.
 *
 * Devuelve `null` cuando no dice nada al respecto — que es casi siempre. En
 * ese caso manda el interruptor de la interfaz, no esto.
 */
export function modoPedido(texto) {
    const t = comparable(texto);
    // El orden importa: "no me contestes en voz" contiene "contestes en voz".
    // Lo que niega se mira primero, y dentro de lo que pide voz, lo más
    // restrictivo antes que lo más amplio.
    if (SIN_VOZ.some((f) => t.includes(f)))
        return "texto";
    if (SOLO_VOZ.some((f) => t.includes(f)))
        return "voz";
    if (CON_VOZ.some((f) => t.includes(f)))
        return "ambas";
    return null;
}
/** ¿Hay que generar audio para esta respuesta? */
export function tocaHablar(modo) {
    return modo === "voz" || modo === "ambas";
}
/**
 * Aquí había un `tocaEscribir`. No lo llamaba nadie y no podía llamarlo nadie:
 * el texto de la respuesta SIEMPRE sale en el chat, se pida como se pida. La
 * función decía que existía un modo «sólo voz» que oculta el texto, y ese modo
 * nunca ha existido — es lo que hacía que el selector de la interfaz tuviera
 * tres botones para dos comportamientos.
 *
 * Se quita en vez de dejarla por si acaso: una función que describe un
 * comportamiento que el programa no tiene es una mentira esperando a que
 * alguien la crea.
 */
/**
 * Lo que se manda a leer en voz alta.
 *
 * El director escribe en markdown: tablas, listas, negritas y enlaces. Leído
 * tal cual suena a "asterisco asterisco cuatro mil quinientos treinta y nueve
 * asterisco asterisco", y una tabla de doce filas dictada no la aguanta nadie.
 *
 * Así que se limpia el formato y, si hay una tabla, se dice cuántas filas
 * tiene en vez de recitarla: para eso está la pantalla.
 */
export function paraLeerEnVozAlta(markdown) {
    const lineas = markdown.split("\n");
    const salida = [];
    let filasDeTabla = 0;
    const soltarTabla = () => {
        if (filasDeTabla > 0) {
            salida.push(filasDeTabla === 1
                ? "Te dejo una fila en la pantalla."
                : `Te dejo la tabla con ${filasDeTabla} filas en la pantalla.`);
            filasDeTabla = 0;
        }
    };
    for (const linea of lineas) {
        const esFilaDeTabla = /^\s*\|.*\|\s*$/.test(linea);
        const esSeparador = /^\s*\|[\s:|-]+\|\s*$/.test(linea);
        if (esFilaDeTabla) {
            if (!esSeparador)
                filasDeTabla++;
            continue;
        }
        soltarTabla();
        salida.push(linea);
    }
    soltarTabla();
    return salida
        .join("\n")
        // Enlaces: se lee el texto, no la dirección.
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        // Encabezados, negritas, cursivas y código.
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1$2")
        .replace(/`{1,3}([^`]*)`{1,3}/g, "$1")
        // Viñetas: el guion inicial se lee como una pausa rara.
        .replace(/^\s*[-*+]\s+/gm, "")
        .replace(/^\s*\d+\.\s+/gm, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
//# sourceMappingURL=voz.js.map